import { dmnIdentity } from "../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../utils/environment";
import { errorHandler, apiFetchNotOkHandler } from "../errorHandler";
import { getBuildVersion } from "../getDefaultHeaders";

export class ConflictError extends Error {
  constructor(message = "Conflict") {
    super(message);
    this.name = "ConflictError";
  }
}

/**
 * Get a download URL for a booking document
 */
export const getDocumentDownloadUrl = async (
  bookingId: string,
  documentId: string
): Promise<string | undefined> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  try {
    const response = await fetch(
      `${environmentVars?.COLLINS_API_BASE_URL}/bookings/${bookingId}/documents/${documentId}/download-url`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Build-Version": getBuildVersion()
        }
      }
    );

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error getting document download URL");
      return undefined;
    }

    const result = await response.json();
    return result?.url ?? result?.download_url;
  } catch (error) {
    errorHandler(error, "Error getting document download URL");
    return undefined;
  }
};

/**
 * Delete a document from a booking
 */
export const deleteDocument = async (
  bookingId: string,
  documentId: string
): Promise<boolean> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  try {
    const response = await fetch(
      `${environmentVars?.COLLINS_API_BASE_URL}/bookings/${bookingId}/documents/${documentId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Build-Version": getBuildVersion()
        }
      }
    );

    if (response.status === 409) {
      throw new ConflictError(
        "This booking has been updated. Please refresh to see the latest changes."
      );
    }

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error deleting document");
      return false;
    }

    return true;
  } catch (error) {
    if (error instanceof ConflictError) {
      throw error;
    }
    errorHandler(error, "Error deleting document");
    return false;
  }
};
